import React, { useEffect, useState } from 'react';
import { ArrowRight } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { apiService, getImageUrl } from '../../config/api';
import { getText } from '../../utils/language';
import { useI18n } from '../../i18n';
import SEO, { SEOConfigs } from '../common/SEO';

const HomePage = () => {
  const [featuredArtworks, setFeaturedArtworks] = useState([]);
  const [heroArtwork, setHeroArtwork] = useState(null);
  const [loading, setLoading] = useState(true);
  const { language, t } = useI18n();

  useEffect(() => {
    let active = true;
    Promise.all([
      apiService.get('/artworks/featured'),
      apiService.get('/artworks/homepage-hero', undefined, { suppressErrorLog: true, fallbackValue: null }),
    ])
      .then(([featuredResponse, heroResponse]) => {
        if (!active) return;
        const featured = Array.isArray(featuredResponse) ? featuredResponse : [];
        setFeaturedArtworks(featured);
        setHeroArtwork(heroResponse?._id ? heroResponse : featured[0] || null);
      })
      .catch((error) => console.error('Error fetching featured artworks:', error))
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, []);

  const featuredArtwork = heroArtwork || featuredArtworks[0];
  const heroImage = featuredArtwork?.images?.[0] || getImageUrl('/Images/1-The Art of Patience_WhatsApp Image 2024-08-12 at 19.03.36.jpeg');
  const featuredTitle = featuredArtwork ? getText(featuredArtwork.title, language, 'Tafsir Art') : 'Tafsir Art';
  const featuredDescription = featuredArtwork ? getText(featuredArtwork.description, language, t('home.subtitle')) : t('home.subtitle');

  return (
    <div className="pt-[var(--header-height)]">
      <SEO {...SEOConfigs.home} />

      <section className="home-hero relative isolate min-h-[calc(100svh-var(--header-height))] overflow-hidden bg-[#171713] text-[#fffaf1]">
        <img
          src={heroImage}
          alt=""
          aria-hidden="true"
          className="home-hero-image absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#171713]/60" aria-hidden="true" />

        <div className="page-shell relative z-10 flex min-h-[calc(100svh-var(--header-height))] flex-col justify-between border-x border-white/25 px-6 py-7 sm:px-10 sm:py-9 lg:px-12 lg:py-10">
          <div className="flex items-start justify-between gap-8 border-b border-white/30 pb-5">
            <p className="text-[11px] font-semibold uppercase tracking-[.2em] text-[#e8b18f]">{t('home.eyebrow')}</p>
            <div className="hidden max-w-xs text-right sm:block">
              <span className="block text-[10px] font-semibold uppercase tracking-[.18em] text-[#e8b18f]">{t('home.featured')}</span>
              <span className="mt-1 block font-display text-xl leading-tight text-[#fffaf1]">{featuredTitle}</span>
            </div>
          </div>

          <div className="home-hero-copy flex flex-1 items-center py-12 sm:py-16">
            <h1 className="max-w-[11ch] font-display text-[clamp(4rem,10vw,9rem)] font-semibold leading-[.78] tracking-[-.055em] text-[#fffaf1]">
              {t('home.title')}
            </h1>
          </div>

          <div className="grid gap-7 border-t border-white/30 pt-6 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
            <div className="max-w-2xl">
              <p className="text-base leading-7 text-[#eee8de] sm:text-lg">{featuredDescription}</p>
              <p className="mt-3 font-display text-xl text-[#e8b18f] sm:hidden">{featuredTitle}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Link to="/gallery" className="inline-flex min-h-[48px] items-center gap-2 border border-[#fffaf1] bg-[#fffaf1] px-5 text-sm font-semibold text-[#171713] transition hover:border-[#e8b18f] hover:bg-[#e8b18f]">
                {t('home.explore')} <ArrowRight size={17} />
              </Link>
              <Link to="/store" className="inline-flex min-h-[48px] items-center border border-white/60 px-5 text-sm font-semibold text-[#fffaf1] transition hover:border-[#fffaf1] hover:bg-white/10">
                {t('home.shop')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="page-shell py-20 lg:py-28">
        <header className="grid gap-5 border-b border-border pb-8 md:grid-cols-[1fr_1fr] md:items-end">
          <div>
            <p className="editorial-eyebrow mb-3">Tafsir Art</p>
            <h2 className="section-title">{t('home.archive')}</h2>
          </div>
          <p className="editorial-lede max-w-xl md:justify-self-end">{t('home.archiveCopy')}</p>
        </header>

        {loading ? (
          <div className="grid gap-5 py-10 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map((item) => <div key={item} className="skeleton-line h-[420px]" />)}</div>
        ) : (
          <div className="grid gap-x-5 gap-y-10 py-10 sm:grid-cols-2 lg:grid-cols-3">
            {featuredArtworks.slice(0, 6).map((artwork, index) => (
              <Link key={artwork._id} to={`/gallery/artwork/${artwork._id}`} className="group block">
                <div className={`overflow-hidden bg-surface ${index % 3 === 1 ? 'h-[430px]' : 'h-[370px]'}`}>
                  <img src={artwork.images?.[0]} alt={getText(artwork.title, language)} className="h-full w-full object-cover transition duration-700 group-hover:scale-[1.025]" />
                </div>
                <div className="border-t border-border py-4">
                  <span className="editorial-eyebrow block text-[10px]">{artwork.category}</span>
                  <h3 className="mt-1 font-display text-2xl font-semibold leading-none">{getText(artwork.title, language)}</h3>
                  {artwork.title?.ar && language !== 'ar' && <p className="mt-1 font-arabic text-lg text-text-secondary" dir="rtl" lang="ar">{artwork.title.ar}</p>}
                </div>
              </Link>
            ))}
          </div>
        )}

        <Link to="/gallery" className="btn-outline">{t('home.viewAll')} <ArrowRight size={17} /></Link>
      </section>

      <section className="border-y border-border bg-surface">
        <div className="page-shell grid lg:grid-cols-2">
          <div className="min-h-[420px] overflow-hidden lg:border-r lg:border-border">
            <img src={getImageUrl('/Images/1-The Art of Patience_WhatsApp Image 2024-08-12 at 19.03.37.jpeg')} alt="Tafsir Art studio work" className="h-full w-full object-cover" loading="lazy" />
          </div>
          <div className="flex flex-col justify-center px-6 py-14 sm:px-12 lg:px-16">
            <p className="editorial-eyebrow mb-4">{t('common.about')}</p>
            <h2 className="section-title max-w-xl">{t('home.story')}</h2>
            <p className="editorial-lede mt-4 max-w-xl">{t('home.storyCopy')}</p>
            <Link to="/about" className="btn-primary mt-8 w-max">{t('home.ourStory')} <ArrowRight size={17} /></Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
